import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { TileIds as TILES } from '@hotjar/webapp/domains/activation/start-page/types';
import {
  useStartPageContent,
  invalidateStartPageContentQuery,
} from '@hotjar/webapp/domains/activation/start-page';
import { useSuggestionsFilters } from '@hotjar/webapp/features/Activation/SpaceOverview/components/SuggestionsBox/useSuggestionsFilters';
import { useDismissTile } from '@hotjar/webapp/features/Activation/SpaceOverview/components/SuggestionsBox/useDismissTile';
import TrackingConsoleErrorsImage from '@hotjar/webapp/features/Activation/SpaceOverview/components/SuggestionsBox/assets/tracking-console-errors.png';
import type { Suggestion } from '@hotjar/webapp/features/Activation/SpaceOverview/components/SuggestionsBox/useSuggestions';
import type { Site } from '@hotjar/site';

interface ErrorReportingSuggestion {
  suggestion: Suggestion | null;
  dismiss: (silent?: boolean) => void;
}

export const useErrorReportingSuggestion = (site: Site): ErrorReportingSuggestion => {
  const { t } = useTranslation();
  const { data: startPageContent } = useStartPageContent();
  const { suggestionsFilters } = useSuggestionsFilters(site);

  const getFilteredStartPageContent = useCallback(
    () => startPageContent?.tiles?.filter(suggestionsFilters),
    [startPageContent?.tiles, suggestionsFilters],
  );

  const { dismiss: dismissErrorReporting } = useDismissTile(TILES.ERROR_REPORTING);

  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);

  const isTileForcedInView = useMemo(
    () => !!getParameter(window.location.href, `force_${TILES.ERROR_REPORTING}_tile`),
    [],
  );

  const dismiss = async (silent = false) => {
    await dismissErrorReporting(silent);
    invalidateStartPageContentQuery();
  };

  const errorReportingSuggestion: Suggestion = useMemo(
    () => ({
      id: TILES.ERROR_REPORTING,
      title: t('activation:space-overview.suggestions.error-reporting.title'),
      description: t('activation:space-overview.suggestions.error-reporting.description'),
      image: TrackingConsoleErrorsImage,
      href: `#`,
    }),
    [t],
  );

  useEffect(() => {
    const allVisibleTiles = getFilteredStartPageContent() ?? null;
    const isErrorReportingTileAvailable = isTileForcedInView
      ? true
      : allVisibleTiles?.some((tileId) => tileId === TILES.ERROR_REPORTING);

    setSuggestion(isErrorReportingTileAvailable ? errorReportingSuggestion : null);
  }, [
    errorReportingSuggestion,
    startPageContent?.tiles,
    isTileForcedInView,
    getFilteredStartPageContent,
  ]);

  return {
    suggestion,
    dismiss,
  };
};
